import { FeedBackVo } from "./dataObjects/FeedBackVo";

export class LineChecker {

    public checkLine(line: string): FeedBackVo {
        let score: number = 30;

        const trimmedSubject = line.trim();
        const subjectWithoutPlaceholders = trimmedSubject.replace(/{.*?}/g, '');
        const subjectWithoutEmojis = subjectWithoutPlaceholders.replace(/[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F1E0}-\u{1F1FF}]/gu, '');
        const words = subjectWithoutEmojis.toLowerCase().split(/\s+/);
        const wordCount = words.length;


        const negFeedback: string[] = [];
        const posFeedback: string[] = [];

        //evaluate the email subject line
        let spamWords = ["free", "win", "winner", "money", "cash", "urgent", "limited time", "offer", "risk-free", "guaranteed"];
        let spamWordsCount = 0;

        const usedSpamWords = spamWords.filter(word => subjectWithoutEmojis.toLowerCase().includes(word));

        for (let i = 0; i < spamWords.length; i++) {
            if (line.toLowerCase().includes(spamWords[i])) {
                spamWordsCount++;
            }
        }

        if (spamWordsCount > 0) {
            score -= spamWordsCount * 5;
            negFeedback.push(`Avoid using spammy words. You used ${spamWordsCount} spammy words.`);
            negFeedback.push(`Spammy words used: ${usedSpamWords.join(', ')}`);
        }
        // Length check
        if (subjectWithoutEmojis.length >= 30 && subjectWithoutEmojis.length <= 60) {
            score += 15;
            posFeedback.push('Optimal length! Your subject line is within the recommended range of 30-60 characters.');
        } else if (subjectWithoutEmojis.length < 30) {
            score += 5;
            posFeedback.push('Your subject line is concise, which can be effective. Consider adding more detail to reach 30-60 characters for optimal impact.');
        } else {
            negFeedback.push('Your subject line is longer than recommended. Try to keep it under 60 characters to prevent truncation in some email clients.');
        }

        if (subjectWithoutEmojis.length < 10) {
            score -= 10;
            negFeedback.push('Your subject line is too short. Consider adding more detail to make it more engaging.');
        }

        // Word variety
        const uniqueWords = new Set(words).size;
        if (uniqueWords / wordCount > 0.8) {
            score += 10;
            posFeedback.push('Good word variety, which can make your subject line more engaging and interesting.');
        }
        else {
            score -= 5;
            negFeedback.push('Consider using more varied vocabulary to make your subject line more engaging.'); // 5 points for low variety
        }

        // Urgency and scarcity
        const urgencyWords = ['limited', 'exclusive', "don't miss", 'last chance', 'ending soon', 'only', 'hurry', 'quick', 'fast', 'now', 'today'];
        const usedUrgencyWords = urgencyWords.filter(word => words.includes(word));
        if (usedUrgencyWords.length > 0) {
            score += 10;
            posFeedback.push('Effective use of urgency or scarcity: ' + usedUrgencyWords.join(', ') + '. This can motivate recipients to open your email.');
        }
        else {
            negFeedback.push('Consider using urgency or scarcity to encourage immediate action. Words like "limited", "exclusive", or "last chance" can be effective.');
        }

        // Emoji usage
        const emojiCount = (trimmedSubject.match(/[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F1E0}-\u{1F1FF}]/gu) || []).length;
        if (emojiCount === 1) {
            score += 5;
            posFeedback.push('Good use of a single emoji. This can increase visual appeal without overwhelming.');
        } else if (emojiCount > 1) {
            negFeedback.push('Multiple emojis detected. While they can add visual interest, use them judiciously to maintain professionalism.');
        }

        // Impactful words
        const impactfulWords = ['new', 'announcement', 'important', 'breaking', 'alert', 'update', 'introducing', 'discover', 'revealed', 'finally'];
        const usedImpactfulWords = impactfulWords.filter(word => words.includes(word));
        if (usedImpactfulWords.length > 0) {
            score += 5;
            posFeedback.push(`Strong word choice: ${usedImpactfulWords.join(', ')}. These words can grab attention and increase open rates.`);
        }
        else {
            negFeedback.push('Consider using impactful words to grab attention. Words like "new", "important", or "discover" can be effective.');
        }

        // Call to action
        const ctaWords = ['get', 'download', 'try', 'buy', 'subscribe', 'learn', 'find out', 'see', 'watch'];
        const usedCtaWords = ctaWords.filter(word => words.includes(word));
        if (usedCtaWords.length > 0) {
            score += 5;
            posFeedback.push('Includes a clear call to action: ' + usedCtaWords.join(', ') + '. This can encourage recipients to engage with your email.');
        }
        else {
            negFeedback.push('Consider including a call to action in your subject line. Words like "get", "download", or "subscribe" can be effective.');
        }

        // Personalization
        if (trimmedSubject.includes('{name}') || trimmedSubject.includes('{company}')) {
            score += 10;
            posFeedback.push('Good use of personalization! This can significantly increase open rates and engagement.');
        }
        else {
            negFeedback.push('Consider personalizing your subject line with the recipient\'s name or company. This can increase open rates and engagement.');
        }

        if (trimmedSubject === trimmedSubject.toUpperCase() && trimmedSubject.length > 10) {
            score -= 10;
            negFeedback.push('Avoid using ALL CAPS as it may trigger spam filters and appear like shouting.');
        }
        //if all lower case and more than 10 characters, add 15 points
        else if (trimmedSubject === trimmedSubject.toLowerCase() && trimmedSubject.length > 10) {
            score += 15;
            posFeedback.push('Using all lowercase can create a casual tone, and can increase open rates.');
        }

        // Question mark
        if (trimmedSubject.includes('?')) {
            score += 5;
            posFeedback.push('Using a question can pique curiosity and boost open rates.');
        }
        else {
            negFeedback.push('Consider using a question in your subject line to pique curiosity and increase open rates.');
        }
        // Exclamation mark


        // Numbers
        if (/\b\d+\b/.test(trimmedSubject)) {
            score += 5;
            posFeedback.push('Including numbers can make your subject line more specific and compelling.');
        }
        else {
            negFeedback.push('Consider including numbers in your subject line. They can make it more specific and compelling.');
        }

        // Final score adjustments
        score = Math.max(0, Math.min(100, score));

        //use from a+ to f- grading system
        const grade = score >= 90 ? 'A+' :
            score >= 85 ? 'A' :
                score >= 80 ? 'A-' :
                    score >= 75 ? 'B+' :
                        score >= 70 ? 'B' :
                            score >= 65 ? 'B-' :
                                score >= 60 ? 'C+' :
                                    score >= 55 ? 'C' :
                                        score >= 50 ? 'C-' :
                                            score >= 45 ? 'D+' :
                                                score >= 40 ? 'D' :
                                                    score >= 35 ? 'D-' : 'F';

        //feedback.push(`Your subject line score is ${score}%. Grade: ${grade}`);

        //add a happy fact to start of every positive feedback
        for (let i = 0; i < posFeedback.length; i++) {
            posFeedback[i] = '😊 ' + posFeedback[i];
        }
        //add a sad fact to every negative feedback
        for (let i = 0; i < negFeedback.length; i++) {
            negFeedback[i] = '😢 ' + negFeedback[i];
        }

        return new FeedBackVo(posFeedback, negFeedback, score, grade);


    }


}